import { useEffect, useRef } from 'react';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';

import { LRQRequest, SuccessResult, UseLRQReturnValue } from 'hooks/useLRQ/useLRQ';
import * as K from 'common/constants';
import { Outcome } from 'common/graphqlTypes';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { networkErrorState } from 'api/apolloClient.state';

// #region  LRQ indication feature

const SEARCH_TIMEOUT_ALERT = {
  id: K.PAGE_ALERT_ID.SEARCH_TIMEOUT,
  message: 'The search timed out, so results may be incomplete',
  variant: PageAlertVariant.ERROR,
  autoHide: false,
};
const LRQ_INDICATION_STEPS_THRESHOLD = 3; // To avoid LRQ indicator "blinking" on short running LRQs

function LRQIndicatorBody(props: {completion: number; onCancel: () => void}): JSX.Element {
  const { completion, onCancel } = props;

  return (
    <div className="LRQRequestManager__LRQIndicator__Body">
      <div className="LRQRequestManager__LRQIndicator__Body__Progress" style={{ width: `${completion}%` }} />
      <span>{`Searched ${completion}%`}</span>
      <Button
        variant={ButtonVariant.BORDERLESS}
        size="sm"
        onClick={onCancel}
        className="LRQRequestManager__LRQIndicator__Body__Action"
      >
        Cancel
      </Button>
    </div>
  );
}


export function useLRQIndication(lrqState: UseLRQReturnValue, request: LRQRequest, showProgress = true): void {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const networkError = useRecoilValue(networkErrorState);
  // Callers should not update the value of showProgress, but we resort to a ref since we can't enforce this contract.
  const showProgressStable = useRef(showProgress);

  const { result, cancel } = lrqState;

  /* effect to set the SEARCH_TIMEOUT_ALERT when any query result has a TIMEOUT outcome */
  useEffect(() => {
    if (
      result?.status === K.QueryStatus.Done
      && (result as SuccessResult)?.results?.some((queryRequestResult) => queryRequestResult?.data?.outcome === Outcome.TIMEOUT)
    ) {
      setPageAlertConfig(SEARCH_TIMEOUT_ALERT);
    }
  }, [ result, setPageAlertConfig ]);

  /* effect to cleanup the page alerts associated with useLRQIndication when the calling component unmounts */
  useEffect(useRecoilCallback(({ set, snapshot }) => (): void => {
    if (
      [K.PAGE_ALERT_ID.SEARCH_PROGRESS, K.PAGE_ALERT_ID.SEARCH_TIMEOUT]
        .includes(snapshot.getLoadable(pageAlertConfigState).getValue().id as K.PAGE_ALERT_ID)
    ) {
      set(pageAlertConfigState, K.NO_PAGE_ALERT);
    }
  }, []), []);

  /**
   * The cleanup function for the following effect should remove the SEARCH_PROGRESS alert only if the calling component
   * passed "showProgress = true". This prevents an interference pattern when multiple LRQs are running. To simplify the
   * chain of effect and callback dependencies, this cleanup function is only created once, and the effect ensures that
   * it won't be called if showProgress was false.
   */
  const searchProgressCleanup = useRecoilCallback(({ set, snapshot }) => (): void => {
    if (snapshot.getLoadable(pageAlertConfigState).getValue().id === K.PAGE_ALERT_ID.SEARCH_PROGRESS) {
      set(pageAlertConfigState, K.NO_PAGE_ALERT);
    }
  }, []);

  /* effect to set the SEARCH_PROGRESS alert if the result is in progress and the calling component has passed showProgress: true */
  useEffect(useRecoilCallback(({ set, snapshot }) => () => {

    /* exit early without cleanup if showProgress is false */
    if (!showProgressStable?.current) {
      return () => {};
    }

    /* exit if result type is not SuccessResult */
    const successResult = result as SuccessResult;
    if (Array.isArray(successResult.results) !== true) {
      return searchProgressCleanup;
    }

    /* exit if LRQ is finished */
    if (request.canceled || [K.QueryStatus.Done, K.QueryStatus.Terminated].includes(successResult.status as K.QueryStatus)) {
      searchProgressCleanup();
      return searchProgressCleanup;
    }

    /* exit if there is not at least one sub-query in the LRQ with totalSteps exceeding the threshold */
    const aboveThreshold = successResult.results.some(({ totalSteps }) => totalSteps >= LRQ_INDICATION_STEPS_THRESHOLD);
    if (!aboveThreshold) {
      return searchProgressCleanup;
    }

    /* exit if SEARCH_TIMEOUT_ALERT is set; the SEARCH_TIMEOUT_ALERT needs to win race conditions */
    if (snapshot.getLoadable(pageAlertConfigState).getValue().id === K.PAGE_ALERT_ID.SEARCH_TIMEOUT) {
      return searchProgressCleanup;
    }

    // set search progress indication
    const completion = Math.floor((successResult.stepsCompleted / successResult.totalSteps) * 100);
    const children = <LRQIndicatorBody completion={completion} onCancel={cancel} />;
    set(pageAlertConfigState, {
      children,
      id: K.PAGE_ALERT_ID.SEARCH_PROGRESS,
      containerClassName: 'LRQRequestManager__LRQIndicator__Container',
    });

    return searchProgressCleanup;
  }, [ result, searchProgressCleanup, showProgressStable ]),
  [ result, searchProgressCleanup, showProgressStable ],
  );
  // This effect cleans up and runs again only when the result changes; searchProgressCleanup and showProgressStable are
  // stable for the lifetime of the component

  useEffect(() => {
    /* On network issues hide the LRQ indication */
    if (networkError) {
      // TODO: Check id here? I am worried this could be cleaning up the PAGE_ALERT_ID.SEARCHING alert though
      setPageAlertConfig(K.NO_PAGE_ALERT);
    }
  }, [networkError, setPageAlertConfig]);

}

// #endregion


/**
 * Hook that cancels a request if any of a supplied list of coordinated requests has been cancelled. This is useful when
 * we are exposing a cancel button in the UI associated with one LRQRequest on a page {@see useLRQIndication}, but there
 * are other requests associated with the same search event which cannot be grouped with the watched request.
 *
 * @param request                The request that we are managing
 * @param cancel                 The cancel function for this request returned by {@see useLRQ}
 * @param coordinatedRequests    If supplied, we watch all of these requests for cancellation, and cancel this request
 */
export function useCoordinatedCancellation(request: LRQRequest, cancel: () => void, coordinatedRequests?: LRQRequest[]): void {
  useEffect(() => {
    if (!coordinatedRequests) {
      return;
    }
    const someRequestCancelled = coordinatedRequests?.some((req) => req?.canceled);
    const thisRequestCancelled = request?.canceled;
    if (someRequestCancelled && !thisRequestCancelled) {
      cancel();
    }
  });
}
